<template>
    <section
        class="stations-header"
        :class="{
            'has-image': station?.image,
        }"
    >
        <div class="inner">
            <div class="illustration">
                <DSFrontifyAsset
                    v-if="station?.image"
                    :asset="station?.image"
                    fill="cover"
                />
                <div class="header-content">
                    <button class="ds-btn ds-btn--secondary btn-back" @click="goBack()">
                        <DSIcon code="arrow-left" set="st1website" size="l" />
                        {{ $t('back') }}
                    </button>
                    <div class="left">
                        <p class="brand">{{ stationBrand }}</p>
                        <h1 class="h1">{{ stationName }}</h1>
                    </div>
                    <nuxt-link
                        v-if="Number(station?.location?.lat) !== 0 && Number(station?.location?.lon) !== 0"
                        :href="`https://maps.google.com/?daddr=${station?.location?.lat},${station?.location?.lon}`"
                        class="directions ds-btn ds-btn--primary"
                        target="_blank"
                    >
                        <span class="label">{{ $t('get-directions') }}</span>
                        <DSIcon
                            size="xl"
                            code="default-line/direction"
                        />
                    </nuxt-link>
                </div>
            </div>
            <BlocksStationsHeaderInfo :station="station" />
        </div>
    </section>
</template>
<script setup>
import { computed, useNuxtApp, navigateTo, useI18n } from '#imports';
import { storeToRefs } from 'pinia';
import { usePageStore } from '~dsc/stores/cms/page';
const { $paths } = useNuxtApp();

const { station } = storeToRefs(usePageStore());
useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            back: 'Back',
        },
        'fi-FI': {
            back: 'Takaisin',
        },
        'sv-SE': {
            back: 'Tillbaka',
        },
        'no-NO': {
            back: 'Tilbake',
        },
    },
});

defineProps({
    image: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    stations: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const stationBrand = computed(() => {
    if (station.value.services?.includes('ploq')) {
        return 'ploq';
    }
    if (station.value.services?.includes('dirtcarwash')) {
        return 'dirt carwash';
    }
    if (station.value.services?.includes('welcomein')) {
        return 'välkommen in';
    }

    return station.value.brand;
});

const stationName = computed(() => {
    const brandRegex = /^St1\s|Shell\s|PLOQ\s|Välkommen\sin\s/;

    return station.value.name?.replace(brandRegex, '') ?? '';
});

const goBack = () => {
    navigateTo($paths.getPath('stationLocator'));
};

</script>
<script>
export default {
    name: 'StationsHeader',
};
</script>
<style scoped>
.stations-header {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.stations-header .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.stations-header .button-container {
  margin: 0 0 2rem;
}
@media (max-width: 1199px) {
  .stations-header .button-container {
    margin-left: 1rem;
  }
}
.stations-header .illustration {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}
.stations-header .illustration > * {
  grid-column-start: 1;
  grid-row-start: 1;
}
.stations-header .illustration .image-container {
  padding-top: 41%;
}
.stations-header .illustration .header-content {
  position: relative;
  z-index: 2;
  display: grid;
  grid-gap: 1rem 0.25rem;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr auto;
  min-height: 100%;
}
.stations-header .illustration .brand {
  text-transform: uppercase;
  font-size: 0.875rem;
  margin-bottom: 0.125rem;
}
.stations-header .illustration .brand,
.stations-header .illustration h1 {
  margin-top: 0;
  text-shadow: 2px 2px 5px var(--color-base-black, 0.08);
}
.stations-header .illustration .btn-back {
  grid-column-end: span 2;
  align-self: start;
  justify-self: start;
}
.stations-header .illustration .left {
  margin-bottom: -0.75rem;
}
.stations-header .illustration .directions {
  align-self: end;
  align-items: center;
}
@media (max-width: 1199px) {
  .stations-header .illustration .left {
    margin-bottom: -0.375rem;
  }
  .stations-header .illustration .directions {
    width: 2.5rem;
    height: 2.5rem;
  }
  .stations-header .illustration .directions .label {
    display: none;
  }
}
.stations-header.has-image .illustration {
  overflow: hidden;
}
.stations-header.has-image .image::before {
  content: "";
  position: absolute;
  pointer-events: none;
  z-index: 1;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: linear-gradient(180deg, transparent 60%, var(--color-base-black));
}
.stations-header.has-image .header-content {
  padding: 2.75rem;
}
.stations-header.has-image .header-content .brand,
.stations-header.has-image .header-content .h1 {
  color: var(--color-base-white);
}
.stations-header.has-image .header-content .right {
  white-space: nowrap;
  padding-right: 0;
}
.stations-header.has-image .header-content .right .ds-btn:hover {
  background: var(--color-primary-300);
}
@media (max-width: 1199px) {
  .stations-header.has-image .header-content {
    padding: 1.5rem;
  }
}
@media (max-width: 991px) {
  .stations-header {
    margin-top: 0;
  }
  .stations-header .inner {
    padding-inline: 0;
  }
  .stations-header .inner .illustration .image-container {
    padding-top: 75%;
  }
  .stations-header .header-direction-link {
    margin-top: 1rem;
  }
}
</style>
